/**
 * PassportTodayCard — SIGNUP-5. The compact Passport capsule at the
 * top of Today, ported from the Claude Design prototype ("Passport ·
 * Dunes / A full wedding season / 3 of 12 booked / Mark a project
 * booked and this moves.").
 *
 * GOAL-TODAY-1 — which goal it picks, in order:
 *   1. the OPEN goal furthest along — always, if there is one
 *   2. else a goal finished in the last 24h and not yet stamped
 *   3. else a goal stamped in the last 24h
 *   4. else the empty state
 * Finishing a goal therefore moves this card on to the next one
 * instead of parking on the finished one until it is stamped. The
 * stamp is never lost — it waits in the Passport, which is the surface
 * built to celebrate it.
 *
 * PASSPORT-2 — it reads `goal_progress` through useGoals(): one goal,
 * its rings, their live values counted on the server. Five states:
 *   fresh      — a goal exists, nothing has moved yet
 *   progress   — rings closing; headline = the ring that needs love
 *   reachable  — every ring is closed (or the server already completed
 *                the goal and the arrival hasn't played): gold glow +
 *                a breathing blue "Launch" button
 *   stamped    — Launch finished (or the goal was stamped today):
 *                passport stamp, "What's next?" → /galaxy
 *   empty      — no goals at all: "Set your first goal."
 *
 * Launch plays the prototype's arrival sequence as a full-screen
 * overlay (Nova leaves the sun, crosses to the world, plants a flag,
 * the passport opens and takes the stamp, the world settles into its
 * orbit), then presses the stamp with passport_stamp — the same one
 * call the galaxy makes. The world it shows is the goal's stored
 * world, so it is the world the galaxy shows.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Nova } from '@/components/nova';
import { PlanetView } from '@/components/passport/planet-view';
import { goalToCore } from '@/components/passport/goal-core';
import { worldOf } from '@/components/galaxy3d/worlds';
import type { Goal } from '@/components/passport/galaxy-core';
import {
  COUNTERS, formatCount, goalPendingStamp, ringDone, ringProgress, useGoals, useStampGoal,
  type Goal as PassportGoal, type GoalRing,
} from '@/services/passport';
import { useOrganization } from '@/services/organizations';

const GOLD = '#BC942F';
const BLUE = '#2F6BE8';
const IVORY = '#F5F3EF';
const DAY_MS = 24 * 60 * 60 * 1000;

const KEYFRAMES = `
@keyframes ptcBlueBreath{0%,100%{box-shadow:0 10px 30px rgba(47,107,232,.45)}50%{box-shadow:0 12px 52px rgba(47,107,232,.8)}}
@keyframes ptcHaloPulse{0%,100%{filter:drop-shadow(0 0 0 rgba(188,148,47,0))}50%{filter:drop-shadow(0 0 22px rgba(188,148,47,.6))}}
@keyframes ptcStampIn{0%{transform:scale(2.4) rotate(-14deg);opacity:0}55%{transform:scale(.94) rotate(-9deg);opacity:1}100%{transform:scale(1) rotate(-9deg);opacity:1}}
@keyframes ptcNovaBob{0%,100%{transform:translateY(0) rotate(0deg)}50%{transform:translateY(-3px) rotate(-4deg)}}
@keyframes ptcRise{from{opacity:0;transform:translateY(14px) scale(.96)}to{opacity:1;transform:none}}
@keyframes ptcFadeIn{from{opacity:0}to{opacity:1}}
`;

export type PassportPreviewState = 'empty' | 'fresh' | 'progress' | 'reachable' | 'stamped';
type Mode = PassportPreviewState;

export function isPassportPreviewState(v: string | null): v is PassportPreviewState {
  return v === 'empty' || v === 'fresh' || v === 'progress' || v === 'reachable' || v === 'stamped';
}

/** DEVMODE-1 — a stand-in goal for Admin → Preview / `?passport=`. */
function previewGoal(state: PassportPreviewState): PassportGoal | null {
  if (state === 'empty') return null;
  const now = new Date().toISOString();
  const value = state === 'fresh' ? 0 : state === 'progress' ? 3 : 12;
  const done = state === 'reachable' || state === 'stamped';
  return {
    id: 'preview-dunes', org_id: 'preview', created_by: null, title: 'A full wedding season', why: 'So the studio can go full-time.', world_id: 'dunes',
    due_on: null, pinned: false, completed_at: done ? now : null, stamped_at: state === 'stamped' ? now : null, archived_at: null, created_at: now, updated_at: now,
    rings: [{ id: 'preview-r0', position: 0, kind: 'counter', label: 'Projects booked', ink: 'bookings', metric: 'bookings', target: 12, closed_at: done ? now : null, value }],
    rings_total: 1, rings_closed: done ? 1 : 0,
  };
}

/** The ring the headline talks about: the first open one, counters first. */
function focusRing(g: PassportGoal): GoalRing | null {
  const open = g.rings.filter((r) => !ringDone(r) && ringProgress(r) < 1);
  return open.find((r) => r.kind === 'counter') ?? open[0] ?? g.rings.find((r) => r.kind === 'counter') ?? g.rings[0] ?? null;
}

function progressLine(g: PassportGoal, r: GoalRing | null) {
  if (!r) return 'Ready when you are';
  if (r.kind === 'counter' && r.metric && r.target) {
    const v = ringDone(r) ? r.target : Math.min(r.value ?? 0, r.target);
    return `${formatCount(r.metric, v)} of ${formatCount(r.metric, r.target)} ${COUNTERS[r.metric].unit}`;
  }
  const closed = g.rings.filter(ringDone).length;
  return `${closed} of ${g.rings.length} steps · next: ${r.label}`;
}

function fillFor(r: GoalRing | null) {
  if (!r) return '';
  if (r.kind === 'counter' && r.metric) return COUNTERS[r.metric].hint.replace(/\.$/, '') + ' — and this moves.';
  return 'Tick it off in your Passport when it’s done.';
}

/** How far along a goal is, 0..1 — closed rings count whole. */
function fractionOf(g: PassportGoal) {
  if (g.rings.length === 0) return 0;
  return g.rings.reduce((s, r) => s + ringProgress(r), 0) / g.rings.length;
}

function stampDate(d = new Date()) {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd} · ${mm} · ${d.getFullYear()}`;
}

function shortDate(d = new Date()) {
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function reducedMotion() {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

// ── Stamp (the same mark the prototype presses into the passport) ──

function StampMark({ world, goal, ink, big }: { world: string; goal?: string; ink: string; big?: boolean }) {
  // Long planet names shrink to stay inside the inner ring.
  const name = world.toUpperCase().slice(0, 18);
  const base = big ? 10 : 12;
  const nameSize = name.length > 8 ? Math.max(5.5, (base * 8) / name.length) : base;
  return (
    <svg viewBox="0 0 100 100" style={{ width: '100%', display: 'block' }}>
      <circle cx="50" cy="50" r="46" fill="none" stroke={ink} strokeWidth={big ? 2.4 : 2.6} strokeDasharray="14 1.5 9 1 22 1.2" />
      <circle cx="50" cy="50" r="38" fill="none" stroke={ink} strokeWidth="1" />
      <text x="50" y="47" textAnchor="middle" fontFamily="-apple-system,sans-serif" fontSize={nameSize} fontWeight="700" letterSpacing={nameSize > 8 ? 1.5 : 0.8} fill={ink}>
        {name}
      </text>
      <text x="50" y={big ? 58 : 60} textAnchor="middle" fontFamily="-apple-system,sans-serif" fontSize={big ? 5.5 : 6.5} letterSpacing="1" fill={ink}>
        {stampDate()}
      </text>
      {big && (
        <>
          <text x="50" y="21" textAnchor="middle" fontFamily="-apple-system,sans-serif" fontSize="4.5" letterSpacing="1.4" fill={ink}>ORBIT · PASSPORT</text>
          {goal && <text x="50" y="86" textAnchor="middle" fontFamily="-apple-system,sans-serif" fontSize="4.5" letterSpacing="1.4" fill={ink}>{goal.toUpperCase().slice(0, 22)}</text>}
        </>
      )}
    </svg>
  );
}

// ── Arrival overlay ────────────────────────────────────────────────

const PHASES: [number, number][] = [[1, 0], [2, 300], [3, 1800], [4, 3300], [5, 4000], [6, 4900]];
const ARRIVAL_MS = 6600;

function polar(r: number, deg: number) {
  const t = (deg * Math.PI) / 180;
  return { x: 50 + r * Math.cos(t), y: 50 + r * Math.sin(t) };
}
const OUTER = polar(34, -50);
const SETTLE = polar(23, -50);

function ArrivalLayer({ goal, studio, phase, progressText, ink, onSkip }: {
  goal: Goal; studio: string; phase: number; progressText: string; ink: string; onSkip: () => void;
}) {
  const [size] = useState(() => Math.min(560, Math.round(window.innerWidth * 0.88)));
  const planetD = Math.round(size * 0.11);
  const novaSize = Math.round(size * 0.1);
  const edge = { x: OUTER.x - 9, y: OUTER.y - 2 };
  const mid = { x: (50 + edge.x) / 2, y: (50 + edge.y) / 2 };
  const nova = phase === 1 ? { x: 50, y: 50 } : phase === 2 || phase === 6 ? mid : edge;
  const planetPos = phase === 6 ? SETTLE : OUTER;
  const label = phase === 6 ? `Visited · ${shortDate()}` : phase >= 3 ? 'Nova has landed' : phase === 2 ? 'In transit' : progressText;
  const novaState = phase >= 4 ? 'celebrating' : phase === 3 ? 'happy' : 'idle';
  const arrivalGoal: Goal = { ...goal, state: phase < 6 ? 'reachable' : 'done', subGoals: goal.subGoals.map((s) => ({ ...s, progress: 1 })) };
  const ease = 'cubic-bezier(.2,.8,.2,1)';
  return (
    <div
      role="dialog"
      aria-label="Nova is arriving at your world"
      className="fixed inset-0 z-[90] flex items-center justify-center"
      style={{ background: 'rgba(8,6,14,.9)', animation: 'ptcFadeIn .5s ease both', color: IVORY }}
    >
      <div style={{ position: 'relative', width: size, height: size }}>
        {[30, 46, 62, 80].map((d, i) => (
          <div key={d} style={{ position: 'absolute', left: '50%', top: '50%', width: `${d}%`, height: `${d}%`, transform: 'translate(-50%,-50%)', borderRadius: '50%', boxShadow: `inset 0 0 0 1px rgba(245,243,239,${i === 3 ? .22 : .12})` }} />
        ))}
        <div style={{ position: 'absolute', left: '50%', top: '50%', width: '12%', aspectRatio: '1', transform: 'translate(-50%,-50%)', borderRadius: '50%', background: 'radial-gradient(circle at 45% 40%,#FFF1B8 0%,#F2D24E 45%,#C9A02E 100%)', boxShadow: '0 0 40px rgba(242,210,78,.55),0 0 120px rgba(242,210,78,.25)' }} />
        <div style={{ position: 'absolute', left: '50%', top: '58%', transform: 'translateX(-50%)', fontSize: 10, letterSpacing: '.2em', textTransform: 'uppercase', color: 'rgba(245,243,239,.5)', whiteSpace: 'nowrap' }}>{studio}</div>

        {phase >= 1 && phase <= 3 && (
          <svg viewBox="0 0 100 100" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} fill="none">
            <line x1="50" y1="50" x2={OUTER.x} y2={OUTER.y} stroke="rgba(188,148,47,.7)" strokeWidth=".35" strokeDasharray="1.2 1.6" />
          </svg>
        )}

        <div style={{ position: 'absolute', left: `${planetPos.x}%`, top: `${planetPos.y}%`, transform: 'translate(-50%,-50%)', transition: `left 1.6s ${ease}, top 1.6s ${ease}` }}>
          <div style={{ position: 'relative', animation: phase < 6 ? 'ptcHaloPulse 5s ease-in-out infinite' : 'none' }}>
            <PlanetView goal={arrivalGoal} diameter={planetD} compact />
            {phase >= 3 && (
              <div style={{ position: 'absolute', left: '50%', top: '4%', width: 2, height: '30%', background: IVORY, animation: 'ptcRise .5s ease-out both' }}>
                <div style={{ position: 'absolute', left: 2, top: 0, width: 12, height: 8, background: GOLD, clipPath: 'polygon(0 0,100% 50%,0 100%)' }} />
              </div>
            )}
          </div>
          <div style={{ position: 'absolute', left: '50%', top: '100%', marginTop: 8, transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.2em', textTransform: 'uppercase', color: GOLD, whiteSpace: 'nowrap' }}>{goal.worldName}</span>
            <span style={{ fontSize: 12, color: 'rgba(245,243,239,.72)', whiteSpace: 'nowrap' }}>{label}</span>
          </div>
        </div>

        <div style={{ position: 'absolute', left: `${nova.x}%`, top: `${nova.y}%`, transform: 'translate(-50%,-50%)', transition: 'left 1.5s cubic-bezier(.4,0,.3,1), top 1.5s cubic-bezier(.4,0,.3,1)' }}>
          <div style={{ animation: 'ptcNovaBob 3.4s ease-in-out infinite' }}><Nova state={novaState} size={novaSize} /></div>
        </div>

        {(phase === 4 || phase === 5) && (
          /* Outer div owns the centring transform; the rise animation
             runs on the inner one so it can't overwrite it. */
          <div style={{ position: 'absolute', left: '50%', top: '50%', width: '36%', transform: 'translate(-50%,-50%)' }}>
            <div style={{ aspectRatio: '3/4', borderRadius: 10, background: '#F0EBDD', boxShadow: '0 30px 80px rgba(0,0,0,.6)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '8%', boxSizing: 'border-box', color: '#3A4152', animation: 'ptcRise .6s ease-out both' }}>
              <div style={{ fontSize: 9, letterSpacing: '.24em', textTransform: 'uppercase', color: '#7A7468' }}>Worlds visited · {new Date().getFullYear()}</div>
              <div style={{ width: '72%', animation: phase === 4 ? 'ptcStampIn .7s cubic-bezier(.2,.9,.3,1.2) both' : 'none', transform: 'rotate(-9deg)' }}>
                <StampMark world={goal.worldName} goal={goal.subGoals[0]?.title} ink={planetInk(goal.skin, true)} big />
              </div>
              <div className="font-editorial italic" style={{ fontSize: 11, textAlign: 'center' }}>“{goal.title}”</div>
            </div>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onSkip}
        className="absolute bottom-6 right-6 rounded-full px-4 py-2 text-xs font-medium text-white/60 hover:text-white hover:bg-white/10 transition-colors"
      >
        Skip
      </button>
    </div>
  );
}

// ── Card ───────────────────────────────────────────────────────────

export function PassportTodayCard({ className = '', preview }: { className?: string; preview?: PassportPreviewState }) {
  const navigate = useNavigate();
  const { data: organization } = useOrganization();
  const studioName = (organization as { name?: string } | undefined)?.name?.trim() || 'Your studio';
  const { data: goals = [], isLoading } = useGoals();
  const stampGoal = useStampGoal();

  const [phase, setPhase] = useState(0);
  const [stampedLocal, setStampedLocal] = useState<string | null>(null);
  const timers = useRef<number[]>([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const pick = useMemo((): { goal: PassportGoal; mode: Mode } | null => {
    if (preview) {
      const goal = previewGoal(preview);
      if (!goal) return null;
      return { goal, mode: stampedLocal === goal.id ? 'stamped' : preview };
    }
    const now = Date.now();
    const newest = (k: (g: PassportGoal) => string | null) => (a: PassportGoal, b: PassportGoal) => (k(b) ?? '').localeCompare(k(a) ?? '');
    const fresh = (iso: string | null) => !!iso && now - new Date(iso).getTime() < DAY_MS;

    // GOAL-TODAY-1 — an OPEN goal wins. This used to be third, behind
    // "finished but not stamped yet", which had no time limit: finish a
    // goal and never press Launch and it owned this card forever,
    // outranking every goal still in play. Rennan: "once completing a
    // goal it stays on today page on both ios and web." He is right,
    // and the answer is that Today moves on — the stamp waits for him
    // in the Passport, where the book and the universe are. Today is a
    // dashboard; it should be showing the work that is still live.
    const active = goals.filter((g) => !g.completed_at).map((goal) => ({ goal, pct: fractionOf(goal) })).sort((a, b) => b.pct - a.pct)[0];
    if (active) {
      const allReached = active.goal.rings.length > 0 && active.goal.rings.every((r) => ringProgress(r) >= 1);
      const mode: Mode = stampedLocal === active.goal.id ? 'stamped' : allReached ? 'reachable' : active.pct === 0 ? 'fresh' : 'progress';
      return { goal: active.goal, mode };
    }

    // Nothing open. Now the finished ones get the card — but only while
    // they are the news. Both windows are 24h, so a goal finished last
    // month doesn't greet you every morning.
    //
    // This path is also the ONLY way to stamp without WebGL 2
    // (PassportFlat mounts this card for exactly that), so it keeps
    // Launch rather than going straight to the empty state.
    const pending = goals.filter((g) => goalPendingStamp(g) && fresh(g.completed_at)).sort(newest((g) => g.completed_at))[0];
    if (pending) return { goal: pending, mode: stampedLocal === pending.id ? 'stamped' : 'reachable' };

    const today = goals.filter((g) => fresh(g.stamped_at)).sort(newest((g) => g.stamped_at))[0];
    if (today) return { goal: today, mode: 'stamped' };

    return null;
  }, [goals, stampedLocal, preview]);

  if (!preview && isLoading && goals.length === 0) return null;

  // ── Empty ────────────────────────────────────────────────────────
  if (!pick) {
    return (
      <Capsule className={className} border="rgba(255,255,255,.12)" glow="none">
        <style>{KEYFRAMES}</style>
        <div className="relative shrink-0" style={{ width: 76, height: 76 }}>
          <div className="absolute inset-2 rounded-full" style={{ border: '1.5px dashed rgba(245,243,239,.35)' }} />
          <div className="absolute inset-[30%] rounded-full" style={{ background: 'radial-gradient(circle at 40% 35%,rgba(245,243,239,.25),rgba(245,243,239,.05))' }} />
        </div>
        {/* GOAL-TODAY-1 — "your first goal" to someone who has already
            finished three reads like the app forgot. */}
        <Copy
          eyebrow="Passport"
          title={goals.length > 0 ? 'Set your next goal.' : 'Set your first goal.'}
          line1="One sentence, and how you’ll know you got there — bookings, paid revenue, new clients, galleries out."
          line2={
            goals.length > 0
              ? 'Your stamps are in the Passport. Pick the next world when you’re ready.'
              : 'Orbit counts the real work. Finish it and the world is stamped.'
          }
        />
        <PrimaryButton onClick={() => navigate('/galaxy?new=1')}>Set a goal</PrimaryButton>
      </Capsule>
    );
  }

  const { goal: g, mode } = pick;
  const rings = g.rings;
  const focus = focusRing(g);
  // The capsule shows the same world the galaxy does: the goal's
  // stored world names it (The Dunes, Aurora Peaks…) and picks the
  // closest 2D skin.
  const world = worldOf(g);
  const goal = goalToCore(g);
  const ink = planetInk(goal.skin);
  const closed = rings.filter((r) => ringDone(r) || ringProgress(r) >= 1).length;
  const progressText = progressLine(g, focus);
  const eyebrow = rings.length > 1 ? `Passport · ${world.name} · ${closed} of ${rings.length} steps` : `Passport · ${world.name}`;

  const finish = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setPhase(0);
    setStampedLocal(g.id);
    if (preview) return; // DEVMODE-1 — nothing real to stamp
    // One call: the server closes reached counters, completes the
    // goal and presses the stamp. Idempotent.
    //
    // GOAL-TODAY-1 — with an onError, because `setStampedLocal` above
    // has already shown the stamp. Without one, a refused write (an
    // org permissions failure, a dropped connection, a goal the server
    // won't close) left the stamp on screen and said nothing, and the
    // goal came back unstamped on the next load. Same shape as the
    // proofing toggles that flipped on a rejected write (DEMO-4): the
    // optimistic state is what makes the silence expensive.
    stampGoal.mutate(g.id, {
      onError: (err) => {
        setStampedLocal(null);
        toast.error((err as Error)?.message || "That stamp didn't save. Try it from your Passport.");
      },
    });
  };

  const launch = () => {
    if (phase !== 0) return;
    if (reducedMotion()) { finish(); return; }
    PHASES.forEach(([ph, t]) => timers.current.push(window.setTimeout(() => setPhase(ph), t)));
    timers.current.push(window.setTimeout(finish, ARRIVAL_MS));
  };

  const reachable = mode === 'reachable';
  const stamped = mode === 'stamped';

  return (
    <>
      <style>{KEYFRAMES}</style>
      <Capsule
        className={className}
        border={reachable ? 'rgba(188,148,47,.7)' : 'rgba(255,255,255,.12)'}
        glow={reachable ? '0 0 60px rgba(188,148,47,.35)' : 'none'}
      >
        <div className="relative shrink-0 flex items-center justify-center" style={{ width: 88, height: 88 }}>
          {stamped ? (
            <div style={{ width: 80, transform: 'rotate(-9deg)', animation: 'ptcStampIn .7s cubic-bezier(.2,.9,.3,1.2) both' }}>
              <StampMark world={goal.worldName} ink={ink} />
            </div>
          ) : (
            <div style={{ animation: reachable ? 'ptcHaloPulse 5s ease-in-out infinite' : 'none' }}>
              <PlanetView goal={{ ...goal, state: reachable ? 'reachable' : 'active' }} diameter={rings.length > 2 ? 34 : rings.length > 1 ? 40 : 48} />
            </div>
          )}
        </div>
        <Copy
          eyebrow={eyebrow}
          title={g.title}
          line1={stamped ? 'Stamped today. Nova’s home.' : reachable ? `${progressText} · Nova’s ready to go` : progressText}
          line2={stamped ? 'The stamp stays for a day, then a fresh world.' : reachable ? 'Every step is done. Launch when you’re ready.' : fillFor(focus)}
        />
        {reachable && <PrimaryButton onClick={launch} breathe>Launch</PrimaryButton>}
        {stamped && <GhostButton onClick={() => navigate('/galaxy')}>What’s next?</GhostButton>}
        {!reachable && !stamped && <GhostButton onClick={() => navigate(preview ? '/galaxy' : `/galaxy?goal=${g.id}`)}>Open Passport</GhostButton>}
      </Capsule>
      {/* Portalled: the capsule's backdrop-filter and the shell's
          rise animation would otherwise turn `position: fixed` into
          "fixed inside this card". */}
      {phase > 0 && createPortal(
        <ArrivalLayer goal={goal} studio={studioName} phase={phase} progressText={progressText} ink={ink} onSkip={finish} />,
        document.body,
      )}
    </>
  );
}

/** Stamp ink per skin — the skin's highlight, so the stamp reads on
 *  the dark capsule (the Passport page's darker inks vanish here). */
function planetInk(skin: Goal['skin'], onPaper = false) {
  if (onPaper) return { ember: '#D4541F', brass: '#B8860B', tide: '#2D6E70', glacier: '#4A6B8C', rose: '#8C5A44', slate: '#3A4152' }[skin] ?? GOLD;
  return { ember: '#E8895B', brass: '#D4A02C', tide: '#5FB3B5', glacier: '#A8C8E8', rose: '#E8A98D', slate: '#9AA4BC' }[skin] ?? GOLD;
}

function Capsule({ children, className, border, glow }: { children: React.ReactNode; className?: string; border: string; glow: string }) {
  return (
    <section
      aria-label="Passport"
      className={`relative flex flex-wrap items-center gap-x-4 gap-y-3 rounded-[28px] px-5 py-4 text-white ${className ?? ''}`}
      style={{
        background: 'rgba(10,14,26,.55)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        boxShadow: `inset 0 1px 0 rgba(255,255,255,.14), inset 0 0 0 1px ${border}, ${glow}`,
        transition: 'box-shadow 1s',
      }}
    >
      {children}
    </section>
  );
}

function Copy({ eyebrow, title, line1, line2 }: { eyebrow: string; title: string; line1: string; line2: string }) {
  return (
    <div className="min-w-0 flex flex-col gap-[3px]" style={{ flex: '1 1 160px' }}>
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.22em', textTransform: 'uppercase', color: GOLD }}>{eyebrow}</div>
      <div className="font-editorial italic truncate" style={{ fontSize: 20, lineHeight: 1.15 }}>{title}</div>
      <div style={{ fontSize: 14, color: 'rgba(245,243,239,.9)', marginTop: 2 }}>{line1}</div>
      {line2 && <div style={{ fontSize: 13, color: 'rgba(245,243,239,.62)' }}>{line2}</div>}
    </div>
  );
}

function PrimaryButton({ children, onClick, breathe }: { children: React.ReactNode; onClick: () => void; breathe?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-14 items-center justify-center whitespace-nowrap rounded-full px-8 text-[17px] font-semibold text-white w-full sm:w-auto"
      style={{ background: BLUE, boxShadow: '0 10px 30px rgba(47,107,232,.45)', animation: breathe ? 'ptcBlueBreath 4s ease-in-out infinite' : 'none' }}
    >
      {children}
    </button>
  );
}

function GhostButton({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-11 items-center justify-center whitespace-nowrap rounded-full px-5 text-sm font-medium transition-colors hover:bg-white/20"
      style={{ background: 'rgba(255,255,255,.12)', color: IVORY }}
    >
      {children}
    </button>
  );
}

export default PassportTodayCard;
